import { createContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { checkToken } from "../services/authService";

export const AuthContext = createContext(null);

const STORAGE_KEY = "token";

export function AuthProvider({ children }) {
  const navigate = useNavigate();

  const [userId, setUserId] = useState(-1);
  const [wallet, setWallet] = useState(null);
  const [walletCurrency, setWalletCurrency] = useState("EUR");
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY));
  const [role, setRole] = useState(null);
  const [roleId, setRoleId] = useState(null);
  const [carrierId, setCarrierId] = useState(null);
  const [currency, setCurrency] = useState("EUR");
  const [invalidated, setInvalidated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) localStorage.setItem(STORAGE_KEY, token);
    else localStorage.removeItem(STORAGE_KEY);
  }, [token]);

  useEffect(() => {
    let cancelled = false;

    const verify = async () => {
      if (!token) {
        if (!cancelled) {
          setUserId(-1);
          setWallet(null);
          setWalletCurrency("EUR");
          setRole(null);
          setRoleId(null);
          setCarrierId(null);
          setCurrency("EUR");
          setInvalidated(false);
          setLoading(false);
        }
        return;
      }

      try {
        const resp = await checkToken(token);

        if (!cancelled) {
          setUserId(resp?.user?.email ?? -1);
          setRoleId(
            resp?.user?.role_id != null
              ? Number(resp.user.role_id)
              : resp?.user?.role?.id != null
                ? Number(resp.user.role.id)
                : null,
          );
          setCarrierId(resp?.user?.carrier_id ?? null);
          setCurrency(resp?.discounts?.cutoff_currency ?? "EUR");
          setRole(resp?.user?.role ?? null);
          setInvalidated(false);
        }
      } catch (e) {
        console.error("[AUTH] checkToken FAILED", e);

        if (!cancelled) {
          setToken(null);
          setUserId(-1);
          setWallet(null);
          setWalletCurrency("EUR");
          setRole(null);
          setRoleId(null);
          setCarrierId(null);
          setCurrency("EUR");
          setInvalidated(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    verify();

    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (invalidated) {
      navigate("/sign-in", { replace: true, state: { reason: "expired" } });
    }
  }, [invalidated, navigate]);

  const logout = () => {
    setToken(null);
    setUserId(-1);
    setWallet(null);
    setWalletCurrency("EUR");
    setRole(null);
    setRoleId(null);
    setCarrierId(null);
    setCurrency("EUR");
    setInvalidated(false);
  };

  const value = useMemo(
    () => ({
      token,
      setToken,
      userId,
      setUserId,
      wallet,
      setWallet,
      walletCurrency,
      setWalletCurrency,
      role,
      setRole,
      roleId,
      setRoleId,
      carrierId,
      setCarrierId,
      currency,
      setCurrency,
      isAuthenticated: !!token,
      logout,
      loading,
    }),
    [
      token,
      userId,
      wallet,
      walletCurrency,
      role,
      roleId,
      carrierId,
      currency,
      loading,
    ],
  );

  return (
    <AuthContext.Provider value={value}>
      {loading ? <div>Loading...</div> : children}
    </AuthContext.Provider>
  );
}
